import React, { useMemo, FC, ComponentProps } from 'react';
import { Box, Typography } from '@altermaind/web/ui';

type Props = {
  text: string;
  size?: number;
  textProps?: ComponentProps<typeof Typography>;
};

/**
 * Component: `InitialsAvatar`
 * `InitialsAvatar` is a React functional component that generates an avatar displaying the initials of a given text.
 * It uses the `useMemo` hook to compute the initials efficiently, ensuring the computation only occurs when the `text` prop changes.
 * The avatar is styled using the `Box` and `Typography` components from the `@altermaind/web/ui` library.
 *
 * @param {Object} props - The props object.
 * @param {string} props.text - The input text from which initials are derived. If the text contains spaces, the initials are taken from the first two words.
 * @param {number} [props.size=60] - The size of the avatar in pixels. Defaults to 60 if not provided.
 * @returns {JSX.Element} A styled avatar displaying the computed initials.
 *
 * ### Dependencies:
 * - React (useMemo, FC)
 * - `@altermaind/web/ui` for `Box` and `Typography` components.
 *
 * ### Example Usage:
 * @example
 * ```jsx
 * import { InitialsAvatar } from './InitialsAvatar';
 *
 * export const App = () => {
 * 	return (
 * 		<Box>
 *   		<InitialsAvatar text="John Doe" size={80} />
 *   		<InitialsAvatar text="Alice" />
 * 		</Box>
 * 	);
 * };
 * ```
 */
export const InitialsAvatar: FC<Props> = ({ text, size = 60, textProps }) => {
  const initials = useMemo(() => {
    if (text.includes(' ')) {
      const first = text.split(' ');
      return first[0].charAt(0) + first[1].charAt(0);
    }
    return text.charAt(0);
  }, [text]);

  return (
    <Box
      backgroundColor='primary'
      display='flex'
      flexDirection='column'
      borderRadius='m'
      alignItems='center'
      justifyContent='center'
      width={size}
      height={size}
    >
      <Typography
        display='inline-flex'
        justifyContent='center'
        alignItems='center'
        variant='special'
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...textProps}
      >
        {initials.toUpperCase()}
      </Typography>
    </Box>
  );
};
